import { useEffect, useState } from "react";
import { peopleApi } from "./api";

// The owner's people lists on Home (Review 14; apps/api/src/people.ts): co-teachers (school accounts
// that get every class, the live view and grading, but not the settings) and guests (up to 5 outside
// emails, let in to demo classes only). Only owners see these cards.

type List = "coteachers" | "guests";

const COPY: Record<List, { title: string; blurb: (domain: string, max: number) => string; placeholder: string; button: string; empty: string }> = {
  coteachers: {
    title: "Co-teachers",
    blurb: (domain) => `Teachers with a @${domain} account who see every class, the live view, submissions and Flagged for you. Only you change settings.`,
    placeholder: "name@christianheritage.org",
    button: "Add co-teacher",
    empty: "No co-teachers yet.",
  },
  guests: {
    title: "Guests",
    blurb: (_domain, max) => `Up to ${max} outside people (any email) who can join a demo class. They never see school classes, and students never see them.`,
    placeholder: "visitor@example.com",
    button: "Add guest",
    empty: "No guests. Nobody from outside the school can sign in.",
  },
};

export function PeopleEntry({ list }: { list: List }) {
  const [emails, setEmails] = useState<string[] | null>(null);
  const [info, setInfo] = useState({ domain: "christianheritage.org", max: 5 });
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const key = list === "coteachers" ? "coTeachers" : "guests";
  useEffect(() => {
    void peopleApi.get().then(
      (r) => {
        setEmails(r[key]);
        setInfo({ domain: r.domain, max: r.maxGuests });
      },
      () => setEmails(null),
    );
  }, [key]);
  if (!emails) return null;
  const copy = COPY[list];
  const full = list === "guests" && emails.length >= info.max;
  return (
    <section aria-label={copy.title} className={`subs-entry people-entry people-${list}`}>
      <div>
        <h2>
          {copy.title}
          {list === "guests" && (
            <span className="muted small">
              {" "}
              {emails.length} of {info.max}
            </span>
          )}
        </h2>
        <p className="muted small">{copy.blurb(info.domain, info.max)}</p>
        {emails.length === 0 ? (
          <p className="muted small">{copy.empty}</p>
        ) : (
          <ul className="people-list">
            {emails.map((e) => (
              <li key={e}>
                <span>{e}</span>
                <button
                  type="button"
                  className="linklike small"
                  aria-label={`Remove ${e}`}
                  onClick={async () => {
                    if (!confirm(`Remove ${e}? They can't sign in as ${list === "guests" ? "a guest" : "a co-teacher"} after this. Nothing they made is deleted.`)) return;
                    setEmails((await peopleApi.remove(list, e))[key]);
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <form
        className="people-add"
        onSubmit={async (ev) => {
          ev.preventDefault();
          setError(null);
          try {
            setEmails((await peopleApi.add(list, draft))[key]);
            setDraft("");
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        <label htmlFor={`add-${list}`} className="sr-only">
          {copy.button}: email
        </label>
        <input id={`add-${list}`} type="email" value={draft} placeholder={copy.placeholder} onChange={(e) => setDraft(e.target.value)} disabled={full} />
        <button type="submit" className="outline small" disabled={!draft.trim() || full}>
          {copy.button}
        </button>
        {error && <p className="dlg-error small">{error}</p>}
      </form>
    </section>
  );
}
